import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, FolderKanban, LogOut, Wrench } from "lucide-react";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const VERT = "#3C4D3A";

export default function ClientHomePage() {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/client-space/me").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div data-testid="client-home-loading" className="flex min-h-screen items-center justify-center bg-ivory"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;
  }

  const projects = data?.projects || [];
  const smallWorks = data?.small_works || [];
  const total = projects.length + smallWorks.length;

  if (total === 1 && projects.length === 1) return <Navigate to={`/espace-client/${projects[0].reference}`} replace />;
  if (total === 1 && smallWorks.length === 1) return <Navigate to={`/espace-client/petits-travaux/${smallWorks[0].reference}`} replace />;

  return (
    <div data-testid="client-home-page" className="min-h-screen bg-ivory font-sans text-navy">
      <header className="border-b border-[#EDEAE3] bg-[#FEFDF9]">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
          <Link to="/"><Logo className="h-8 w-auto" /></Link>
          <div className="flex items-center gap-2">
            <span className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white" style={{ backgroundColor: VERT }}>Espace Client</span>
            <button data-testid="client-home-logout" onClick={async () => { await logout(); window.location.assign("/acces-phase3"); }} aria-label="Déconnexion" className="rounded-full border border-stone-200 p-2 text-stone-500 hover:text-navy"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-12">
        <h1 className="font-display text-3xl font-medium tracking-tight sm:text-4xl">Bienvenue{data?.client?.name ? `, ${data.client.name}` : ""}</h1>
        {total === 0 ? (
          <p data-testid="client-home-empty" className="mt-4 max-w-lg text-base leading-relaxed text-stone-600">
            Votre interlocuteur CozyHome prépare actuellement votre dossier. Votre projet apparaîtra ici dès sa création.
          </p>
        ) : (
          <>
            <p className="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-stone-400">Mes dossiers</p>
            <div className="mt-4 space-y-3">
              {projects.map((p) => (
                <Link key={p.id} to={`/espace-client/${p.reference}`} data-testid={`client-home-project-${p.reference}`} className="flex items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-white p-5 transition-colors hover:border-navy/30">
                  <span className="flex min-w-0 items-center gap-3">
                    <FolderKanban className="h-5 w-5 shrink-0" style={{ color: VERT }} />
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-navy">{p.name}</span>
                      <span className="block text-xs text-stone-500">{p.phase} · accompagné par {p.partner_name}</span>
                    </span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-stone-400" />
                </Link>
              ))}
              {smallWorks.map((s) => (
                <Link key={s.id} to={`/espace-client/petits-travaux/${s.reference}`} data-testid={`client-home-smallwork-${s.reference}`} className="flex items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-white p-5 transition-colors hover:border-navy/30">
                  <span className="flex min-w-0 items-center gap-3">
                    <Wrench className="h-5 w-5 shrink-0 text-cognac" />
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-navy">{s.need || "Petits travaux"}</span>
                      <span className="block text-xs text-stone-500">{s.step}</span>
                    </span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-stone-400" />
                </Link>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
